/**
 * DCFilterPresets - Filter preset management for Dixon-Coles page
 *
 * Allows users to save, load, and manage custom filter configurations.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  useDCFilterPresets,
  createDCFilterPreset,
  updateDCFilterPreset,
  deleteDCFilterPreset,
} from '../../swr-config';
import type { DCFilters, DCFilterPreset } from '../../types';

interface Props {
  currentFilters: DCFilters;
  onLoadPreset: (filters: DCFilters) => void;
  shouldFetchPresets?: boolean;
}

export function DCFilterPresets({ currentFilters, onLoadPreset, shouldFetchPresets = false }: Props) {
  const { presets, mutate } = useDCFilterPresets(shouldFetchPresets);
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [editingPreset, setEditingPreset] = useState<DCFilterPreset | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowSaveDialog(false);
        setEditingPreset(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSavePreset = async () => {
    if (!newPresetName.trim()) {
      setError('Please enter a preset name');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createDCFilterPreset(newPresetName.trim(), currentFilters);
      await mutate();
      setNewPresetName('');
      setShowSaveDialog(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save preset');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePreset = async (preset: DCFilterPreset) => {
    setSaving(true);
    setError(null);

    try {
      await updateDCFilterPreset(preset.id, { filters: currentFilters });
      await mutate();
      setEditingPreset(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update preset');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = async (presetId: string) => {
    if (!confirm('Delete this preset?')) return;

    try {
      await deleteDCFilterPreset(presetId);
      await mutate();
    } catch (err: any) {
      setError(err.message || 'Failed to delete preset');
    }
  };

  const handleLoadPreset = (preset: DCFilterPreset) => {
    onLoadPreset(preset.filters);
    setIsOpen(false);
  };

  return (
    <div className="dc-presets" ref={dropdownRef}>
      <button
        type="button"
        className="dc-presets-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M2 4h12M2 8h12M2 12h8" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
        Presets
        {presets.length > 0 && (
          <span className="dc-presets-count">{presets.length}</span>
        )}
        <svg
          className={`toggle-chevron ${isOpen ? 'expanded' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
        >
          <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {isOpen && (
        <div className="dc-presets-dropdown">
          {error && (
            <div className="dc-presets-error">{error}</div>
          )}

          {/* Save current filters section */}
          {showSaveDialog ? (
            <div className="dc-presets-save-form">
              <input
                type="text"
                placeholder="Preset name..."
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                maxLength={50}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSavePreset();
                  if (e.key === 'Escape') {
                    setShowSaveDialog(false);
                    setNewPresetName('');
                  }
                }}
              />
              <div className="dc-presets-save-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowSaveDialog(false);
                    setNewPresetName('');
                    setError(null);
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-save"
                  onClick={handleSavePreset}
                  disabled={saving || !newPresetName.trim()}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="dc-presets-save-btn"
              onClick={() => setShowSaveDialog(true)}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
              Save current filters
            </button>
          )}

          {/* Presets list */}
          {presets.length > 0 && (
            <>
              <div className="dc-presets-divider" />
              <div className="dc-presets-list">
                {presets.map((preset) => (
                  <div key={preset.id} className="dc-preset-item">
                    <button
                      type="button"
                      className="dc-preset-load"
                      onClick={() => handleLoadPreset(preset)}
                      title={`Load "${preset.name}"`}
                    >
                      <span className="dc-preset-name">{preset.name}</span>
                    </button>
                    <div className="dc-preset-actions">
                      {editingPreset?.id === preset.id ? (
                        <>
                          <button
                            type="button"
                            className="dc-preset-action cancel"
                            onClick={() => setEditingPreset(null)}
                            title="Cancel"
                            disabled={saving}
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12">
                              <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="dc-preset-action confirm"
                            onClick={() => handleUpdatePreset(preset)}
                            title="Save current filters to this preset"
                            disabled={saving}
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12">
                              <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="dc-preset-action update"
                            onClick={() => setEditingPreset(preset)}
                            title="Update with current filters"
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12">
                              <path d="M2 10h2l6-6-2-2-6 6v2zM7 3l2 2" stroke="currentColor" strokeWidth="1" fill="none" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            className="dc-preset-action delete"
                            onClick={() => handleDeletePreset(preset.id)}
                            title="Delete preset"
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12">
                              <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {presets.length === 0 && !showSaveDialog && (
            <div className="dc-presets-empty">
              No saved presets yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}
